import i18n from "i18next";
export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  categorySlug: string;
  tags: string[];
  author: {
    name: string;
    avatar: string;
    role: string;
    bio: string;
  };
  date: string;
  image: string;
  popular: boolean;
  accommodationSlugs: string[]; // Slugs of recommended rooms to display at the bottom
  views?: number;
}

export const blogCategories = [
  { name: "Blog", slug: "blog" }
];

const rawBlogPosts: BlogPost[] = [
{
  id: 1,
  title: "Understanding Massage and Facial Treatments: Benefits for Body and Mind",
  slug: "understanding-massage-and-facial-treatments",
  excerpt: "Learn about massage and facial treatments, their wellness benefits, and why they are essential for relaxation and self-care.",
  content: `<p><a href="/"><strong>Understanding Massage and Facial Treatments: Benefits for Body and Mind</strong></a> - In today's busy world, taking time to care for your physical and mental well-being is more important than ever. Among the most popular wellness experiences are massage therapy and facial treatments. These timeless practices not only help the body relax but also promote healthier skin, improved circulation, and a greater sense of balance.</p>

  <p>Whether you are looking to relieve stress, rejuvenate your appearance, or simply enjoy a peaceful moment away from daily responsibilities, understanding the benefits of massage and facial treatments can help you make the most of your wellness journey.</p>

  <h2>What Is Massage Therapy?</h2>

  <p>Massage therapy involves the skilled manipulation of muscles, connective tissues, and pressure points throughout the body. Practiced for centuries across many cultures, massage is designed to promote relaxation, reduce muscle tension, improve circulation, and support overall wellness.</p>

  <p>There are many different massage techniques available today, including traditional Balinese massage, deep tissue massage, aromatherapy massage, hot stone therapy, and reflexology. Each treatment offers unique benefits depending on your individual needs.</p>

  <h2>What Is a Facial Treatment?</h2>

  <p>A facial is a professional skincare treatment that focuses on cleansing, exfoliating, nourishing, and revitalizing the skin. Depending on your skin type and concerns, facial treatments may include cleansing, steaming, exfoliation, extraction, masks, moisturizers, and facial massage techniques.</p>

  <p>Regular facials help maintain healthy skin while also providing a deeply relaxing experience that can reduce stress and improve overall confidence.</p>

  <h2>5 Benefits of Massage Therapy</h2>

  <h3>1. Reduces Stress and Anxiety</h3>
  <p>Massage helps lower cortisol levels while stimulating the release of endorphins, creating a calming effect that reduces stress and promotes emotional well-being.</p>

  <h3>2. Relieves Muscle Tension</h3>
  <p>Targeted massage techniques help release tight muscles, improve flexibility, and ease physical discomfort caused by travel, exercise, or long hours of sitting.</p>

  <h3>3. Improves Blood Circulation</h3>
  <p>Enhanced circulation helps deliver oxygen and nutrients throughout the body more efficiently, supporting recovery and overall health.</p>

  <h3>4. Enhances Sleep Quality</h3>
  <p>The relaxation achieved during massage often contributes to deeper, more restorative sleep and improved recovery.</p>

  <h3>5. Supports Overall Wellness</h3>
  <p>Regular massage can improve mood, increase energy levels, and contribute to a healthier lifestyle by helping the body maintain balance and relaxation.</p>

  <h2>5 Benefits of Facial Treatments</h2>

  <h3>1. Deep Cleanses the Skin</h3>
  <p>Professional facials remove dirt, oil, and impurities that can accumulate beneath the skin's surface, helping to maintain a clearer complexion.</p>

  <h3>2. Improves Skin Hydration</h3>
  <p>Facials replenish moisture levels, leaving the skin softer, smoother, and more radiant.</p>

  <h3>3. Enhances Skin Texture and Tone</h3>
  <p>Exfoliation helps remove dead skin cells, revealing fresher, brighter, and more even-looking skin.</p>

  <h3>4. Stimulates Healthy Circulation</h3>
  <p>Facial massage techniques encourage blood flow, helping the skin achieve a healthy natural glow.</p>

  <h3>5. Promotes Relaxation</h3>
  <p>Facial treatments provide a calming experience that allows guests to disconnect from daily stress and focus on self-care.</p>

  <h2>Experience Complete Wellness at Patal Kikian Spa</h2>

  <p>After understanding the many benefits of massage and facial treatments, there is no better place to experience them than <strong><a href="/news/understanding-massage-and-facial-treatments">Patal Kikian Spa</a></strong>. Designed as a sanctuary of tranquility, the spa combines traditional Balinese hospitality with professional wellness treatments tailored to each guest's individual needs.</p>

  <p>From soothing Balinese massages and aromatherapy rituals to rejuvenating facial treatments, every experience at Patal Kikian Spa is thoughtfully crafted to restore balance, relax the body, and refresh the mind. Surrounded by the peaceful atmosphere of Sidemen, guests can fully immerse themselves in a journey of wellness and relaxation.</p>

  <p>Whether you are staying at Patal Kikian Villas or visiting Sidemen for a wellness retreat, Patal Kikian Spa offers the perfect opportunity to unwind, recharge, and experience the healing traditions of Bali. Treat yourself to a moment of complete relaxation and discover why wellness is an essential part of the Balinese way of life.</p>`,
  category: "Blog",
  categorySlug: "blog",
  tags: [
    "Spa",
    "Massage",
    "Facial",
    "Wellness",
    "Relaxation",
    "Sidemen",
    "Patal Kikian Spa"
  ],
  author: {
    name: "Patal Kikian Villas Team",
    avatar: "/media/patal-kikian-hero-banner.webp",
    role: "Administrator",
    bio: "Our teams loves sharing knowledge about Balinese hospitality, wellness traditions, local culture, and unforgettable experiences in Sidemen."
  },
  date: "June 22, 2026",
  image: "/media/patal-spa.webp",
  popular: true,
  accommodationSlugs: [
    "private-pool-villa-mountain-view",
    "suite-room-mountain-view",
    "deluxe-bungalow-mountain-view"
  ]
},
{
  id: 2,
  title: "A Taste of Bali: Traditional Ingredients and Iconic Balinese Dishes",
  slug: "taste-of-bali-traditional-ingredients-and-iconic-dishes",
  excerpt: "Discover the essential ingredients behind Balinese cuisine, the island's most beloved dishes, and where to enjoy an unforgettable dining experience at Patal Kikian Restaurant.",
  content: `<p><a href="/"><strong>A Taste of Bali: Traditional Ingredients and Iconic Balinese Dishes</strong></a> - Balinese cuisine is more than just food—it's a reflection of the island's culture, traditions, and centuries-old culinary heritage. Every dish tells a story, combining fresh local ingredients, aromatic spices, and traditional cooking techniques that have been passed down through generations. Whether enjoyed during family gatherings, temple ceremonies, or daily meals, Balinese food is known for its rich flavors and unforgettable aromas.</p>

  <p>At the heart of almost every traditional Balinese dish lies <em>Base Genep</em>, often referred to as Bali's complete spice blend. This aromatic mixture forms the foundation of many of the island's most famous recipes and creates the distinctive taste that makes Balinese cuisine unique.</p>

  <h2>The Essential Ingredients of Balinese Cuisine</h2>

  <p>Balinese cooking relies heavily on fresh herbs, spices, and locally sourced ingredients. While recipes vary from village to village, several ingredients appear consistently throughout traditional Balinese kitchens.</p>

  <h3>1. Shallots and Garlic</h3>
  <p>These aromatic ingredients provide the savory foundation of countless Balinese dishes. They are commonly ground into spice pastes and marinades.</p>

  <h3>2. Turmeric</h3>
  <p>Known for its vibrant yellow color and earthy flavor, turmeric adds depth to curries, rice dishes, and ceremonial foods.</p>

  <h3>3. Ginger and Galangal</h3>
  <p>These fragrant roots contribute warmth, spice, and complexity to many traditional recipes.</p>

  <h3>4. Lemongrass</h3>
  <p>Fresh lemongrass delivers a bright citrus aroma that enhances soups, grilled meats, and spice blends.</p>

  <h3>5. Chili Peppers</h3>
  <p>Balinese cuisine often features varying levels of heat, with fresh chilies providing both spice and flavor.</p>

  <h3>6. Candlenut</h3>
  <p>This traditional ingredient adds richness and texture to spice pastes while helping bind flavors together.</p>

  <h3>7. Coconut</h3>
  <p>Used in many forms, including grated coconut, coconut milk, and coconut oil, this versatile ingredient is essential to Balinese cooking.</p>

  <h3>8. Kaffir Lime Leaves and Local Herbs</h3>
  <p>These aromatic elements add freshness and complexity to many traditional dishes.</p>

  <h2>Traditional Balinese Dishes You Must Try</h2>

  <p>Using these flavorful ingredients, Balinese chefs create some of Indonesia's most celebrated culinary specialties.</p>

  <h3>Babi Guling</h3>
  <p>Often considered Bali's most famous dish, Babi Guling is a whole roasted suckling pig marinated with traditional spices before being slowly cooked until perfectly tender and crispy.</p>

  <h3>Ayam Betutu</h3>
  <p>This beloved chicken dish is marinated with Base Genep, wrapped in banana leaves, and slow-cooked for hours, resulting in exceptionally rich and aromatic flavors.</p>

  <h3>Bebek Betutu</h3>
  <p>Similar to Ayam Betutu but prepared with duck, this traditional delicacy is known for its tender meat and deeply infused spices.</p>

  <h3>Sate Lilit</h3>
  <p>One of Bali's most unique dishes, Sate Lilit combines minced meat or seafood with coconut and spices before being wrapped around bamboo or lemongrass sticks and grilled over charcoal.</p>

  <h3>Lawar</h3>
  <p>A traditional mixture of vegetables, coconut, herbs, and meat, Lawar is often prepared during ceremonies and family celebrations.</p>

  <h3>Nasi Campur Bali</h3>
  <p>This popular dish features steamed rice served with a variety of small portions including vegetables, meats, sambal, and traditional side dishes, offering a complete taste of Bali on a single plate.</p>

  <h2>Experience Balinese Flavors at Patal Kikian Restaurant</h2>

  <p>After discovering the ingredients and traditional dishes that define Balinese cuisine, there is no better place to enjoy them than <strong>Patal Kikian Restaurant</strong>. Inspired by authentic island flavors and local culinary traditions, the restaurant offers a carefully curated menu that celebrates the best of Bali.</p>

  <p>Among our most popular signature dishes is the legendary <strong>Patal Kikian Crispy Duck</strong>. Prepared using selected spices and traditional cooking techniques, the duck is marinated to perfection before being cooked until the skin becomes irresistibly crispy while the meat remains tender and flavorful inside. Served alongside traditional Balinese accompaniments, it has quickly become a favorite among both local and international guests.</p>

  <p>Whether you are craving authentic Balinese specialties, fresh local ingredients, or a memorable dining experience surrounded by the beauty of Sidemen, Patal Kikian Restaurant invites you to discover the true taste of Bali.</p>

  <h2>Reserve Your Table Today</h2>

  <p>Our signature Patal Kikian Crispy Duck is one of the most requested dishes on the menu, and seating is often limited during lunch and dinner hours. To ensure the best dining experience, we highly recommend making a reservation in advance.</p>

  <p>Gather your family, friends, or loved ones and enjoy an unforgettable culinary journey inspired by Bali's rich heritage. <strong>Reserve your table today at Patal Kikian Restaurant and experience the authentic flavors of Bali in a warm and welcoming atmosphere.</strong></p>`,
  category: "Blog",
  categorySlug: "blog",
  tags: [
    "Balinese Food",
    "Bali Cuisine",
    "Restaurant",
    "Sidemen",
    "Crispy Duck",
    "Patal Kikian Restaurant"
  ],
  author: {
    name: "Patal Kikian Villas Team",
    avatar: "/media/patal-kikian-hero-banner.webp",
    role: "Administrator",
    bio: "Our teams loves sharing knowledge about Balinese hospitality, culinary traditions, local culture, and unforgettable experiences in Sidemen."
  },
  date: "June 22, 2026",
  image: "/media/patal-kikian-restaurant (1).webp",
  popular: true,
  accommodationSlugs: [
    "private-pool-villa-mountain-view",
    "suite-room-mountain-view",
    "deluxe-bungalow-mountain-view"
  ]
}
];


export const blogPosts: BlogPost[] = rawBlogPosts.map((post, index) => ({
  ...post,
  views: 1000 - index * 65, // Base default views descending (e.g. 1000, 935, 870...)
}));

export const getLocalizedPost = (post: BlogPost): BlogPost => {
  const title = i18n.t(`news.posts.${post.slug}.title`, post.title);
  const excerpt = i18n.t(`news.posts.${post.slug}.excerpt`, post.excerpt);
  const content = i18n.t(`news.posts.${post.slug}.content`, post.content);
  
  return {
    ...post,
    title: title !== `news.posts.${post.slug}.title` ? title : post.title,
    excerpt: excerpt !== `news.posts.${post.slug}.excerpt` ? excerpt : post.excerpt,
    content: content !== `news.posts.${post.slug}.content` ? content : post.content,
  };
};

export function getAllPosts(): BlogPost[] {
  return [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(getLocalizedPost);
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  const post = blogPosts.find(post => post.slug === slug);
  return post ? getLocalizedPost(post) : undefined;
}

export function getPostsByCategory(categorySlug: string): BlogPost[] {
  return blogPosts
    .filter(post => post.categorySlug === categorySlug)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map(getLocalizedPost);
}

export function searchPosts(query: string): BlogPost[] {
  const lowercaseQuery = query.toLowerCase().trim();
  if (!lowercaseQuery) return [];
  return blogPosts
    .filter(post => 
      post.title.toLowerCase().includes(lowercaseQuery) ||
      post.excerpt.toLowerCase().includes(lowercaseQuery) ||
      post.content.toLowerCase().includes(lowercaseQuery) ||
      post.category.toLowerCase().includes(lowercaseQuery) ||
      post.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    )
    .map(getLocalizedPost);
}

export function recordPostView(slug: string) {
  try {
    const key = `blog_view_count_${slug}`;
    const current = parseInt(localStorage.getItem(key) || "0", 10);
    localStorage.setItem(key, (current + 1).toString());
  } catch (e) {
    console.error("Failed to record view:", e);
  }
}

export function getPopularPosts(): BlogPost[] {
  return [...blogPosts]
    .map(post => {
      let localViews = 0;
      try {
        const key = `blog_view_count_${post.slug}`;
        localViews = parseInt(localStorage.getItem(key) || "0", 10);
      } catch (e) {
        // Safe fail
      }
      return { ...post, views: (post.views || 0) + localViews };
    })
    .sort((a, b) => b.views - a.views)
    .map(getLocalizedPost);
}

export function getAllCategories() {
  return blogCategories.map(cat => {
    const count = blogPosts.filter(post => post.categorySlug === cat.slug).length;
    return { ...cat, count };
  }).filter(cat => cat.count > 0);
}
